/*
 * A JSON call to a Nexus /auth/* endpoint with the current bearer token, retried once across a
 * silent refresh on 401 (the hand-off step-up poll can outlive a short access token). Never throws
 * for an HTTP status: callers branch on {status, body.code}. Mirrors Helm web Web/src/lib/authCall.ts.
 */

import { GATEWAY_BASE } from './config';
import { getToken, refreshAccessToken } from './apiClient';
import { hasStepUpAmr } from './handoff';

export interface AuthReply { status: number; body: Record<string, unknown> }
export type AuthCaller = (method: 'GET' | 'POST', path: string, body?: unknown) => Promise<AuthReply>;

export const codeOfReply = (r: AuthReply): string | undefined => (typeof r.body.code === 'string' ? r.body.code : undefined);

export const authCall: AuthCaller = async (method, path, body) => {
  const send = async (): Promise<AuthReply> => {
    const res = await fetch(`${GATEWAY_BASE}/auth/${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${getToken() ?? ''}` },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    let j: unknown = {};
    try { const t = await res.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; }
    return { status: res.status, body: j && typeof j === 'object' && !Array.isArray(j) ? (j as Record<string, unknown>) : {} };
  };
  let r = await send();
  if (r.status === 401 && (await refreshAccessToken())) r = await send();
  return r;
};

/**
 * L2: the step-up poll said ALREADY_USED — an earlier poll consumed the approval and its answer
 * was lost. The session row has it, so refresh; a new token with amr mfa / handoff_headset is the
 * success we missed. Null otherwise. Mirrors Helm web recoverAfterUsed (tested there).
 */
export async function recoverAfterUsed(): Promise<string | null> {
  const t = await refreshAccessToken();
  return t && hasStepUpAmr(t) ? t : null;
}
